import type { CatalogV2Channel } from '@skupreme/api-contracts/core';
import { Tooltip } from '@skupreme/shared/ui';
import {
  CHANNEL_TONE_BADGES,
  type ChannelHealth,
  type ChannelHealthItem,
  channelHealth,
  channelHealthText,
} from '../../lib/channelHealth';
import { marketplaceChannels, marketplaceChannelText } from '../../lib/v2';
import { ChannelMark } from './ChannelMark';
import { CountryFlagBadge } from './CountryFlagBadge';
import { StatusDot } from './StatusDot';

/* The hover text: the channel, its state by the dot, then a line per warning. */
function HealthTooltip({
  text,
  health,
}: {
  text: string;
  health: ChannelHealth;
}) {
  return (
    <div className="sk:flex sk:flex-col sk:gap-0.5">
      <span>{text}</span>
      <span className="sk:inline-flex sk:items-center sk:gap-1.5">
        <StatusDot tone={CHANNEL_TONE_BADGES[health.tone]} />
        {health.label}
      </span>
      {health.warnings.map(warning => (
        <span key={warning}>{warning}</span>
      ))}
    </div>
  );
}

/**
 * The item's marketplaces as the legacy list draws them: one logo per connection and country, its details on hover.
 * M2: a dot on each logo says whether its listings are live, priced and stocked (lib/channelHealth), a flag where they
 * sell.
 */
export function MarketplaceChannels({
  channels,
  item,
  countries,
}: {
  channels: CatalogV2Channel[];
  /** The item the listings sell: its availability and catalog state feed the warnings. */
  item: ChannelHealthItem;
  /**
   * Only these countries' logos (ISO codes); all when unset or empty. A marketplace with no country (an online store
   * such as Shopify) is always drawn: it has no country for the picker to exclude it by.
   */
  countries?: readonly string[];
}) {
  const marketplaces = marketplaceChannels(channels).filter(
    marketplace =>
      !countries?.length ||
      marketplace.country === null ||
      countries.includes(marketplace.country),
  );

  /* No marketplace reads as an empty cell, not a dash. */
  if (!marketplaces.length) {
    return null;
  }

  return (
    <div className="sk:flex sk:flex-wrap sk:gap-1.5">
      {marketplaces.map(marketplace => {
        const text = marketplaceChannelText(marketplace);
        const health = channelHealth(marketplace, item);

        return (
          <Tooltip
            key={marketplace.key}
            content={<HealthTooltip text={text} health={health} />}
          >
            <span
              role="img"
              aria-label={channelHealthText(text, health)}
              className="sk:relative sk:inline-flex"
            >
              <ChannelMark
                channel={{
                  provider: marketplace.provider,
                  display_name: marketplace.label,
                }}
              />
              <StatusDot
                tone={CHANNEL_TONE_BADGES[health.tone]}
                className="sk:absolute sk:-top-0.5 sk:-right-0.5"
              />
              {marketplace.country && (
                <CountryFlagBadge
                  country={marketplace.country}
                  className="sk:absolute sk:-bottom-0.5 sk:-left-0.5"
                />
              )}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}
